/**
 * Cáscara de la aplicación: sesión, navegación, estado de conexión y sincronización.
 *
 * El indicador de estado (sin conexión / sincronizando / pendientes / conflicto) es
 * persistente y accesible desde cualquier pantalla, porque el cajero necesita saber
 * en todo momento si lo que ve está confirmado o no.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SyncSummary } from "@senm/domain";
import { formatInstant } from "@senm/domain";
import { useConnectivity } from "./hooks/useConnectivity.js";
import { hasUnsyncedWork, wipeLocalData } from "./local/db.js";
import {
  clearSession,
  evaluateOfflineAccess,
  getSession,
  offlineRemainingHours,
  sessionCan,
  type StoredSession,
} from "./local/session.js";
import {
  lastSyncedAt,
  outboxSummary,
  purgeSyncedOutbox,
  recoverAfterRestart,
  synchronize,
} from "./sync/engine.js";
import { precargarGeneradorPdf } from "./services/payments.js";
import { AcademicCatalogPage } from "./pages/AcademicCatalog.js";
import { AdminPage } from "./pages/Admin.js";
import { ConceptsPage } from "./pages/Concepts.js";
import { ConflictsPage } from "./pages/Conflicts.js";
import { DashboardPage } from "./pages/Dashboard.js";
import { LoginPage } from "./pages/Login.js";
import { ConsolidatedReceiptPage } from "./pages/ConsolidatedReceipt.js";
import { CobroFamiliarPage } from "./pages/CobroFamiliar.js";
import { ReportsPage } from "./pages/Reports.js";
import { StudentsAdminPage } from "./pages/StudentsAdmin.js";
import { StudentsPage } from "./pages/Students.js";
import { SyncCenterPage } from "./pages/SyncCenter.js";

export type Route =
  | "panel"
  | "estudiantes"
  | "sincronizacion"
  | "conflictos"
  | "reportes"
  | "cobro-familiar"
  | "comprobante-consolidado"
  | "conceptos"
  | "gestion-estudiantes"
  | "catalogos"
  | "administracion";

const RUTAS: { id: Route; etiqueta: string; permiso: string | null }[] = [
  { id: "panel", etiqueta: "Panel", permiso: null },
  { id: "estudiantes", etiqueta: "Estudiantes", permiso: "estudiantes:leer" },
  {
    id: "sincronizacion",
    etiqueta: "Sincronización",
    permiso: "sync:leer_resumen",
  },
  { id: "conflictos", etiqueta: "Conflictos", permiso: "conflictos:leer" },
  { id: "reportes", etiqueta: "Reportes", permiso: "reportes:leer" },
  {
    id: "cobro-familiar",
    etiqueta: "Cobro a hermanos",
    // Cobra de verdad, así que pide el permiso de registrar pagos y no el de
    // reimprimir: no es una pantalla de papeles, es una ventanilla.
    permiso: "pagos:registrar",
  },
  {
    id: "comprobante-consolidado",
    etiqueta: "Comprobante consolidado",
    // Mismo permiso que reimprimir un recibo: el consolidado no crea nada, vuelve a
    // imprimir comprobantes de pagos que ya existen.
    permiso: "recibos:reimprimir",
  },
  {
    id: "conceptos",
    etiqueta: "Conceptos",
    permiso: "configuracion:administrar",
  },
  {
    id: "gestion-estudiantes",
    etiqueta: "Gestión de estudiantes",
    permiso: "usuarios:administrar",
  },
  {
    id: "catalogos",
    etiqueta: "Ciclos y grados",
    permiso: "configuracion:administrar",
  },
  {
    id: "administracion",
    etiqueta: "Administración",
    permiso: "usuarios:leer",
  },
];

export function App(): JSX.Element {
  const [session, setSession] = useState<StoredSession | null>(null);
  const [cargando, setCargando] = useState(true);
  const [ruta, setRuta] = useState<Route>("panel");
  const [resumen, setResumen] = useState<SyncSummary | null>(null);
  const [ultimaSync, setUltimaSync] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  /**
   * Contador que avanza al terminar cada sincronización. Las pantallas lo usan como
   * dependencia para releer de IndexedDB: sin esto, un pago confirmado por el
   * servidor seguiría mostrándose como «pendiente» hasta cambiar de vista.
   */
  const [tickSync, setTickSync] = useState(0);
  const [aviso, setAviso] = useState<{
    tono: "ok" | "aviso" | "error";
    texto: string;
  } | null>(null);
  const [actualizacionDisponible, setActualizacionDisponible] = useState(false);

  const { state: conexion } = useConnectivity();
  const ahora = new Date().toISOString();

  const refrescarEstado = useCallback(async () => {
    setResumen(await outboxSummary());
    setUltimaSync(await lastSyncedAt());
  }, []);

  useEffect(() => {
    void (async () => {
      const almacenada = await getSession();
      setSession(almacenada);
      // Recuperación tras un cierre abrupto: nada queda atrapado en "enviando".
      const recuperadas = await recoverAfterRestart(new Date().toISOString());
      if (recuperadas > 0) {
        setAviso({
          tono: "aviso",
          texto: `Se recuperaron ${recuperadas} operación(es) que quedaron a medio enviar. Se reintentarán.`,
        });
      }
      await refrescarEstado();
      setCargando(false);
    })();
  }, [refrescarEstado]);

  // El generador de PDF se precarga en cuanto hay sesión: el recibo provisional
  // debe poder emitirse aunque la conexión se corte antes del primer cobro.
  useEffect(() => {
    if (session !== null) precargarGeneradorPdf();
  }, [session]);

  useEffect(() => {
    const onActualizacion = (): void => setActualizacionDisponible(true);
    window.addEventListener("senm:actualizacion-disponible", onActualizacion);
    return () =>
      window.removeEventListener(
        "senm:actualizacion-disponible",
        onActualizacion,
      );
  }, []);

  const sincronizar = useCallback(async () => {
    if (session === null || sincronizando) return;
    setSincronizando(true);
    setAviso(null);
    try {
      const resultado = await synchronize(new Date().toISOString());
      if (resultado.revoked) {
        // El servidor retiró la autorización: se corta el acceso y se limpia el dispositivo.
        await clearSession();
        await wipeLocalData();
        setSession(null);
        setAviso({
          tono: "error",
          texto: "Su acceso fue revocado. Comuníquese con Administración.",
        });
        return;
      }
      if (resultado.offline) {
        setAviso({
          tono: "aviso",
          texto:
            "Sin conexión. Los pagos quedan guardados y se enviarán al reconectar.",
        });
        return;
      }
      const partes: string[] = [];
      if (resultado.accepted > 0)
        partes.push(`${resultado.accepted} sincronizada(s)`);
      if (resultado.duplicated > 0)
        partes.push(`${resultado.duplicated} ya estaba(n) registrada(s)`);
      if (resultado.conflicted > 0)
        partes.push(`${resultado.conflicted} en revisión`);
      if (resultado.rejected > 0)
        partes.push(`${resultado.rejected} rechazada(s)`);
      setAviso({
        tono:
          resultado.conflicted > 0 || resultado.rejected > 0 ? "aviso" : "ok",
        texto:
          partes.length > 0
            ? `Sincronización: ${partes.join(", ")}.`
            : "Todo está sincronizado.",
      });
    } catch {
      setAviso({
        tono: "error",
        texto: "No fue posible sincronizar. Se reintentará automáticamente.",
      });
    } finally {
      setSincronizando(false);
      // Se purgan las operaciones ya sincronizadas y antiguas. Sin esto, IndexedDB
      // crece sin límite y cada arranque lee una cola cada vez más larga.
      await purgeSyncedOutbox(new Date().toISOString());
      await refrescarEstado();
      setTickSync((valor) => valor + 1);
    }
  }, [session, sincronizando, refrescarEstado]);

  // Sincronización automática al recuperar la conexión.
  //
  // Las dependencias son deliberadamente `conexion` y el usuario, NO `sincronizar`:
  // esa función se recrea en cada render y la incluiría en un bucle infinito. El
  // efecto debe dispararse al CAMBIAR el estado de conexión, no en cada render.
  const usuarioActivo = session?.user.id ?? null;
  useEffect(() => {
    if (conexion === "EN_LINEA" && usuarioActivo !== null)
      void sincronizarRef.current?.();
  }, [conexion, usuarioActivo]);

  // Referencia siempre apuntando a la última versión de `sincronizar`.
  const sincronizarRef = useRef<(() => Promise<void>) | null>(null);
  sincronizarRef.current = sincronizar;

  const acceso = useMemo(
    () => evaluateOfflineAccess(session, ahora),
    [session, ahora],
  );

  const cerrarSesion = useCallback(async () => {
    const pendiente = await hasUnsyncedWork();
    if (pendiente) {
      const confirmar = window.confirm(
        "Hay operaciones sin sincronizar. Si cierra la sesión y borra los datos, se perderán.\n\n¿Desea cerrar la sesión de todas formas?",
      );
      if (!confirmar) return;
    }
    await clearSession();
    await wipeLocalData();
    setSession(null);
  }, []);

  if (cargando) {
    return (
      <main>
        <p className="vacio">Cargando…</p>
      </main>
    );
  }

  if (session === null || !acceso.allowed) {
    return (
      <LoginPage
        motivo={session !== null && !acceso.allowed ? "VENCIDA" : null}
        aviso={aviso}
        conexion={conexion}
        onIniciada={async (nueva) => {
          setSession(nueva);
          setAviso(null);
          await refrescarEstado();
        }}
      />
    );
  }

  const rutasVisibles = RUTAS.filter(
    (item) => item.permiso === null || sessionCan(session, item.permiso),
  );
  const horasOffline = offlineRemainingHours(session, ahora);

  return (
    <div className="marco">
      <a className="saltar" href="#contenido">
        Saltar al contenido
      </a>
      <header className="barra">
        <span className="marca">FOLIO</span>
        <EstadoConexion
          conexion={conexion}
          sincronizando={sincronizando}
          resumen={resumen}
          ultimaSync={ultimaSync}
          timeZone={session.user.timeZone}
        />
        <nav className="menu" aria-label="Secciones">
          {rutasVisibles.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setRuta(item.id)}
              aria-current={ruta === item.id ? "page" : undefined}
            >
              {item.etiqueta}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void sincronizar()}
            disabled={sincronizando}
          >
            {sincronizando ? "Sincronizando…" : "Sincronizar"}
          </button>
          <button type="button" onClick={() => void cerrarSesion()}>
            Salir
          </button>
        </nav>
      </header>

      <main id="contenido">
        {actualizacionDisponible && (
          <div className="aviso-caja" role="status">
            <strong>Hay una versión nueva disponible.</strong> Sus pagos
            pendientes se conservan.{" "}
            <button
              type="button"
              className="boton"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("senm:aplicar-actualizacion"),
                )
              }
            >
              Actualizar ahora
            </button>
          </div>
        )}

        {aviso !== null && (
          <div
            className={`aviso-caja ${aviso.tono === "ok" ? "ok" : aviso.tono === "error" ? "error" : ""}`}
            role="status"
          >
            {aviso.texto}
          </div>
        )}

        {conexion === "SIN_CONEXION" && (
          <div className="aviso-caja" role="status">
            <strong>Trabajando sin conexión.</strong> Puede consultar y
            registrar pagos; se enviarán al reconectar. Su acceso sin conexión
            vence en {horasOffline} hora(s).
          </div>
        )}

        {ruta === "panel" && (
          <DashboardPage
            session={session}
            resumen={resumen}
            ultimaSync={ultimaSync}
            onIr={setRuta}
          />
        )}
        {ruta === "estudiantes" && (
          <StudentsPage
            session={session}
            conexion={conexion}
            onCambio={refrescarEstado}
            tickSync={tickSync}
          />
        )}
        {ruta === "sincronizacion" && (
          <SyncCenterPage
            session={session}
            onSincronizar={sincronizar}
            sincronizando={sincronizando}
          />
        )}
        {ruta === "conflictos" && (
          <ConflictsPage session={session} conexion={conexion} />
        )}
        {ruta === "reportes" && (
          <ReportsPage session={session} conexion={conexion} />
        )}
        {ruta === "cobro-familiar" && (
          <CobroFamiliarPage
            session={session}
            conexion={conexion}
            onCambio={refrescarEstado}
            tickSync={tickSync}
          />
        )}
        {ruta === "comprobante-consolidado" && (
          <ConsolidatedReceiptPage session={session} conexion={conexion} />
        )}
        {ruta === "conceptos" && (
          <ConceptsPage session={session} conexion={conexion} />
        )}
        {ruta === "gestion-estudiantes" && (
          <StudentsAdminPage
            session={session}
            conexion={conexion}
            onCambio={sincronizar}
          />
        )}
        {ruta === "catalogos" && (
          <AcademicCatalogPage session={session} conexion={conexion} />
        )}
        {ruta === "administracion" && (
          <AdminPage session={session} conexion={conexion} />
        )}
      </main>
    </div>
  );
}

function EstadoConexion(props: {
  conexion: string;
  sincronizando: boolean;
  resumen: SyncSummary | null;
  ultimaSync: string | null;
  timeZone: string;
}): JSX.Element {
  const { conexion, sincronizando, resumen, ultimaSync, timeZone } = props;

  // Cada estado lleva SÍMBOLO + TEXTO: no se depende del color para comunicarlo.
  const conexionTexto =
    conexion === "EN_LINEA"
      ? "● En línea"
      : conexion === "SIN_CONEXION"
        ? "⚠ Sin conexión"
        : "… Comprobando";
  const conexionTono =
    conexion === "EN_LINEA" ? "ok" : conexion === "SIN_CONEXION" ? "aviso" : "";

  /*
   * Cuánto hace que no se TRAEN datos del servidor.
   *
   * «Todo sincronizado» solo miraba la cola de salida: decía que todo estaba
   * bien aunque la copia local fuera de hace horas. Dos veces hizo pensar que
   * algo se había perdido —«inscribí a una alumna y no me aparece»— cuando lo
   * único que pasaba era que este dispositivo no había vuelto a bajar nada. No
   * hay sincronización automática por tiempo, así que eso puede durar.
   *
   * El cálculo usa el reloj del render, que avanza con cada sincronización y
   * cada cambio de pantalla. No hace falta más precisión para decir «esto ya
   * tiene un rato».
   */
  const minutosDesdeLaBajada =
    ultimaSync === null
      ? null
      : Math.floor((Date.now() - Date.parse(ultimaSync)) / 60_000);
  const datosViejos =
    minutosDesdeLaBajada !== null && minutosDesdeLaBajada >= 15;
  const antiguedad =
    minutosDesdeLaBajada === null
      ? ""
      : minutosDesdeLaBajada < 60
        ? `hace ${minutosDesdeLaBajada} min`
        : `hace ${Math.floor(minutosDesdeLaBajada / 60)} h`;

  return (
    <div className="pila" role="status" aria-live="polite">
      <span className={`marca-estado ${conexionTono}`}>{conexionTexto}</span>
      {sincronizando && <span className="marca-estado">↻ Sincronizando</span>}
      {resumen !== null && resumen.pending > 0 && (
        <span className="marca-estado aviso">
          ◷ {resumen.pending} pendiente(s)
        </span>
      )}
      {resumen !== null && resumen.conflicted > 0 && (
        <span className="marca-estado error">
          ! {resumen.conflicted} en revisión
        </span>
      )}
      {resumen !== null && resumen.rejected > 0 && (
        <span className="marca-estado error">
          ✕ {resumen.rejected} rechazada(s)
        </span>
      )}
      {resumen !== null &&
        resumen.pending === 0 &&
        resumen.conflicted === 0 &&
        resumen.rejected === 0 &&
        (datosViejos ? (
          // Enviado sí está todo; traído no. Decir «Todo sincronizado» aquí es
          // lo que hacía creer que un alta reciente se había perdido.
          <span className="marca-estado aviso">
            ✓ Enviado · pulse Sincronizar para traer lo nuevo
          </span>
        ) : (
          <span className="marca-estado ok">✓ Todo sincronizado</span>
        ))}
      <span
        className={`marca-estado ${datosViejos ? "aviso" : ""}`}
        title="Última vez que este dispositivo trajo datos del servidor"
      >
        Datos al{" "}
        {ultimaSync === null
          ? "nunca sincronizado"
          : `${formatInstant(ultimaSync, timeZone)}${antiguedad === "" ? "" : ` (${antiguedad})`}`}
      </span>
    </div>
  );
}
